import * as React from "react";
import { ArticleStatListItem } from "../types";
import { FilterState, defaultFilterState } from "../articles/types";
import useApi from "@/hooks/useApi";

const FILTER_STATE_KEY = "kb:articles:filterState";

export default function useGroupedArticles() {
  const [articles, setArticles] = React.useState<ArticleStatListItem[]>([]);
  const [loadingArticles, setLoadingArticles] = React.useState(true);
  const [searchError, setSearchError] = React.useState<string | null>(null);

  const [filterState, setFilterState] =
    React.useState<FilterState>(defaultFilterState);
  const api = useApi();

  const [filterStateLoaded, setFilterStateLoaded] = React.useState(false);

  const fetchArticles = React.useCallback(async () => {
    setLoadingArticles(true);

    try {
      // Convert empty strings to null so ASP.NET Core can deserialize int? and string? fields cleanly
      const payload = {
        ...filterState,
        topicId: filterState.topicId || null,
        lastEditedByUserId: filterState.lastEditedByUserId || null,
      };
      const response = await api.admin.post("api/kb/articles/search", payload);
      const json = await response.json();
      // API serializes with camelCase — read both forms for safety
      const articlesData = json?.articles ?? json?.Articles;
      const errorMsg = json?.error ?? json?.Error;
      if (errorMsg) {
        console.error("KB articles search error from server:", errorMsg);
      }
      setArticles(Array.isArray(articlesData) ? articlesData : []);
      setSearchError(errorMsg ?? null);
    } catch (err) {
      console.error("Failed to fetch KB articles:", err);
      setArticles([]);
      setSearchError(String(err));
    } finally {
      setLoadingArticles(false);
    }
  }, [filterState]);

  /**
   * fetch articles when filter state changes
   */

  React.useEffect(() => {
    if (filterStateLoaded) {
      fetchArticles();
    }
  }, [filterStateLoaded, fetchArticles]);

  /**
   * load filter state from localStorage
   */

  React.useEffect(() => {
    const json = localStorage.getItem(FILTER_STATE_KEY);

    if (!json) {
      setFilterStateLoaded(true);
      return;
    }

    try {
      const state = JSON.parse(json);

      // Visibility filter was added later, migrate the state if it's not there.

      if (state.visibility === undefined) {
        state.visibility = "All";
      }

      setFilterState(state);
    } catch (err) {
      console.error(err);
    } finally {
      setFilterStateLoaded(true);
    }
  }, []);

  /** persist filterState when it changes */

  React.useEffect(() => {
    localStorage.setItem(FILTER_STATE_KEY, JSON.stringify(filterState));
  }, [filterState]);

  return {
    articles,
    loadingArticles,
    searchError,
    filterState,
    setFilterState
  };
}
